const React = window.React;
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import Player from './player';
import Song from './song';
import VoiceSwitcher from './voice_switcher';

// Completed plays of the same excerpt before offering the premium voice: the
// second one means the listener came back to it.
const PLAYS_BEFORE_SUGGESTION = 2;
// Silence at the keyboard, after having listened, that reads as being stuck.
const IDLE_AFTER_TYPING = 10000;

class Playlist extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      current: {
        song: null,
        track: null,
        collapsed: null,
        listen: false
      },
      dictation: props.dictation,
      first_reading: props.first_reading,
      proof_reading: props.proof_reading,
      voice: props.voice || 'margot',
      premiumUrls: this.buildPremiumUrls(props),
      previewsLeft: props.previews_left,
      loading: false,
      paywallOpen: false,
      suggestionShown: false,
      hasListened: false,
      replays: {}
    };

    // Not state: only read when the tab comes back, never rendered.
    this.leftTab = false;

    // In-flight premium audio requests, keyed by track: two plays of the same
    // excerpt share one request instead of racing for the generation lock.
    this.pendingTracks = {};

    this.toggleListening = this.toggleListening.bind(this);
    this.changeVoice = this.changeVoice.bind(this);
    this.replay = this.replay.bind(this);
    this.onCollapse = this.onCollapse.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onListen = this.onListen.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.setCurrentTime = this.setCurrentTime.bind(this);
    this.play = this.play.bind(this);
    this.playPause = this.playPause.bind(this);
    this.pause = this.pause.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.handleKeyBinding = this.handleKeyBinding.bind(this);
  }

  componentDidMount = () => {
    document.addEventListener("keydown", this.handleKeyBinding);
    document.addEventListener("keydown", this.handleTyping);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  componentWillUnmount = () => {
    document.removeEventListener("keydown", this.handleKeyBinding);
    document.removeEventListener("keydown", this.handleTyping);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    clearTimeout(this.idleTimer);
  }

  componentWillReceiveProps(nextProps) {
    // You don't have to do this check first, but it can help prevent an unneeded render
    if (nextProps.dictation !== this.state.dictation) {
      this.setState({
        dictation: nextProps.dictation
      });
    }
  }

  // Seeds the premium URLs already generated server-side. A chunk that is not
  // generated yet is absent here and requested from the endpoint on first play.
  buildPremiumUrls = props => {
    const urls = {};

    if (props.premium_first_reading) urls['first_reading'] = props.premium_first_reading.url;
    if (props.premium_proof_reading) urls['proof_reading'] = props.premium_proof_reading.url;

    let index = 0;
    (props.premium_dictation || []).forEach(sentence => (sentence || []).forEach(chunk => {
      index++;
      if (chunk) urls[`dictation:${index}`] = chunk.url;
    }));

    return urls;
  };

  // Global 1-based chunk index, the one the filenames and the API use.
  trackIndex = track => {
    if (!track || track.type !== 'dictation') return null;

    let index = 0;
    for (let sentence = 0; sentence < this.state.dictation.length; sentence++) {
      for (let chunk = 0; chunk < this.state.dictation[sentence].length; chunk++) {
        index++;
        if (sentence + 1 === track.sentence && chunk + 1 === track.chunk) return index;
      }
    }

    return null;
  };

  trackKey = track => track.type === 'dictation' ? `dictation:${this.trackIndex(track)}` : track.type;

  currentSrc = () => {
    const track = this.state.current.track;
    if (!track) return null;
    if (this.state.voice === 'margot') return track.url;

    return this.state.premiumUrls[this.trackKey(track)] || null;
  };

  csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
  };

  post = (url, body, method = 'POST') => fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': this.csrfToken()
    },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  });

  changeVoice (voice, { refused = false } = {}) {
    this.setState({ voice }, () => {
      if (voice === 'arthur' && this.state.current.track) this.ensurePremiumTrack(this.state.current.track);
    });

    this.post(this.props.voice_endpoint, { voice, refused }, 'PUT')
      .then(response => response.json())
      .then(data => this.setState({ previewsLeft: data.previews_left }))
      .catch(() => {});
  }

  // Resolves to a playable URL, or null when the paywall stopped us.
  //
  // Every resolved URL is memoised under the track key, the fallback ones
  // included: currentSrc() then returns something and the caller stops asking.
  // Without that, a fallback answer would send listen() straight back here and
  // hammer the endpoint. In-flight requests are shared for the same reason.
  ensurePremiumTrack (track) {
    const key = this.trackKey(track);
    const known = this.state.premiumUrls[key];
    if (known) return Promise.resolve(known);
    if (this.pendingTracks[key]) return this.pendingTracks[key];

    this.setState({ loading: true });

    const request = this.post(this.props.audio_endpoint, {
      text_id: this.props.text_id,
      kind: track.type,
      index: this.trackIndex(track)
    }).then(response => response.json().then(data => ({ status: response.status, data })))
      .then(({ status, data }) => {
        this.setState(prevState => ({
          loading: false,
          previewsLeft: data.previews_left === undefined ? prevState.previewsLeft : data.previews_left
        }));

        if (status === 402) {
          this.openPaywall();
          return null;
        }

        // Provider down or lock held: this excerpt keeps Margot's voice rather
        // than failing in the listener's face.
        const url = data.url || data.fallback;
        if (!url) return null;

        return new Promise(resolve => {
          this.setState(
            prevState => ({ premiumUrls: { ...prevState.premiumUrls, [key]: url } }),
            () => resolve(url)
          );
        });
      })
      .catch(() => {
        this.setState({ loading: false, voice: 'margot' });
        return null;
      })
      .then(url => {
        delete this.pendingTracks[key];
        return url;
      });

    this.pendingTracks[key] = request;

    return request;
  }

  countReplay = track => {
    if (!track || !this.canSuggest()) return;

    const key = this.trackKey(track);
    const count = (this.state.replays[key] || 0) + 1;

    this.setState(prevState => ({ replays: { ...prevState.replays, [key]: count } }));

    if (count >= PLAYS_BEFORE_SUGGESTION) this.openSuggestion('difficulty');
  };

  suggestPremiumVoice = () => this.openSuggestion('misunderstood', { once: false });

  // Someone who listened and is stuck is the listener the premium voice is for.
  canSuggest = () => (
    this.props.suggestions_enabled !== false &&
    this.state.voice === 'margot' &&
    this.state.hasListened &&
    !this.props.corrected &&
    !this.state.suggestionShown &&
    !this.state.paywallOpen
  );

  // Typing anywhere on the page counts: the dictation editor is a Draft.js
  // contenteditable, and its keystrokes bubble up to the document.
  handleTyping = () => {
    this.hasTyped = true;
    this.scheduleIdleSuggestion();
  };

  // Restarted on every keystroke, and paused while audio plays: someone
  // listening is not stuck, they are working.
  scheduleIdleSuggestion = () => {
    clearTimeout(this.idleTimer);

    if (!this.hasTyped || this.state.current.inProgress || !this.canSuggest()) return;

    this.idleTimer = setTimeout(this.suggestWhenStuck, IDLE_AFTER_TYPING);
  };

  suggestWhenStuck = () => {
    if (!this.canSuggest()) return;

    this.pause();
    this.openSuggestion('misunderstood');
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.leftTab = true;
      return;
    }

    if (!this.leftTab) return;

    this.leftTab = false;
    this.suggestWhenStuck();
  };

  // `once` marks the suggestion as spent, so the automatic triggers fire at most
  // once per page. The button the listener presses themselves does not.
  openSuggestion = (kind, { once = true } = {}) => {
    const html = (this.props.suggestions_html || {})[kind];
    if (!this.openModal(html, { onConfirm: this.acceptSuggestion, onDecline: this.declineSuggestion })) return;

    if (once) this.setState({ suggestionShown: true });
  };

  acceptSuggestion = () => {
    this.closeModal();
    this.changeVoice('arthur');
  };

  // "Non merci" is remembered server side, so the offer stays away for 30 days
  // rather than coming back on the next dictation.
  declineSuggestion = () => {
    this.setState({ suggestionShown: true });
    this.changeVoice('margot', { refused: true });
  };

  // Everything goes through the site's own modal, filled the way the Turbo
  // Stream modals fill it (#modal): rendering our own box would drift from the
  // modals the rest of the site shows.
  openModal = (html, { onConfirm = null, onDecline = null } = {}) => {
    const container = document.querySelector('.js-modal-container');
    if (!container || !html) return false;

    container.innerHTML = html;

    const bind = (selector, handler) => {
      if (!handler) return;
      const element = container.querySelector(selector);
      if (element) element.addEventListener('click', handler);
    };

    bind('.js-try-arthur', onConfirm);
    bind('.js-decline-arthur', onDecline);

    if (window.Modal) window.Modal.open();

    return true;
  };

  closeModal = () => {
    if (window.Modal) window.Modal.close();
  };

  openPaywall = () => {
    this.openModal(this.props.paywall_html);
    this.setState({ paywallOpen: true });
    // Arthur is out of reach: back to Margot, and the refusal is stamped so the
    // suggestion popups stop offering what the listener cannot have.
    this.changeVoice('margot', { refused: true });
  };

  handleKeyBinding = event => {
    if (document.querySelector('.js-sign-in-modal') || !document.body.contains(this.refs.playlist)) return;

    var keyCode = event.keyCode;
    if (keyCode == 13) { // Play / pause : Enter
      this.playPause();
      event.stopPropagation();
      event.preventDefault();
    } else if (keyCode == 117) { // Prev : F6
      this.prev();
      event.stopPropagation();
      event.preventDefault();
    } else if (keyCode == 9 || keyCode == 118) { // Next : Tab / F7
      this.next();
      event.stopPropagation();
      event.preventDefault();
    }
  }

  toggleListening (song = null, track = null, listen = true) {
    const callback = track ? listen ? this.listen : this.pause : null;
    this.setState({
      current: {
        song,
        track,
        collapsed: song,
        inProgress: false,
        progress: 0,
        duration: 0
      }
    }, callback);
  }

  replay (song, track = null) {
    if (!track) {
      let updatedDictation = this.state.dictation.slice(0);
      updatedDictation[song - 2] = updatedDictation[song - 2].map(chunk => {
        delete chunk.progress;
        delete chunk.duration;
        return chunk;
      });
      track = updatedDictation[song - 2][0];
    }

    this.setState({
      current: {
        song,
        track,
        collapsed: song,
        inProgress: false,
        progress: 0,
        duration: 0
      }
    }, track ? this.listen : null);
  }

  onCollapse (song) {
    this.setState(prevState => ({
      current: {
        ...prevState.current,
        collapsed: song
      }
    }));
  }

  onLoadedMetadata(event) {
    if (!this.state.current || !this.state.current.track) return;

    let updatedDictation = this.state.dictation.slice(0);

    if (this.state.current.track.type === "dictation") {
      updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1] = {
        ...updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1],
        duration: event.target.duration || 0
      }
    } else {
      const state = { ...this.state };
            state[this.state.current.track.type].duration = event.target.duration || 0;
      this.setState(state);
    }

    this.setState(prevState => ({
      current: {
        ...prevState.current,
        duration: event.target.duration || 0
      },
      dictation: updatedDictation
    }));
  }

  onPlay (event) {
    // Listening is not being stuck: the idle countdown waits for silence.
    this.scheduleIdleSuggestion();

    this.setState(prevState => ({
      // Gates the exit suggestion: offering the premium voice to someone who
      // never pressed play would be noise.
      hasListened: prevState.hasListened || prevState.voice === 'margot',
      current: {
        ...prevState.current,
        inProgress: true
      }
    }));
  }

  onListen (progress) {
    let updatedDictation = this.state.dictation.slice(0);

    this.setState(prevState => {
      if (this.state.current.track.chunk) {
        updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1] = {
          ...updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1],
          progress
        }

        prevState.dictation = updatedDictation;
      } else {
        prevState[this.state.current.track.type].progress = progress;
      }

      prevState.current = {
        ...prevState.current,
        progress
      }

      return prevState;
    });
  }

  onEnded (event) {
    this.countReplay(this.state.current.track);
    this.scheduleIdleSuggestion();

    this.setState(prevState => {
      if (this.state.current.track.chunk) {
        let updatedDictation = this.state.dictation.slice(0);
        updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1] = {
          ...updatedDictation[this.state.current.track.sentence - 1][this.state.current.track.chunk - 1],
          progress: event.target.duration
        }

        prevState.dictation = updatedDictation;
      } else {
        prevState[this.state.current.track.type].progress = event.target.duration;
      }

      prevState.current = {
        ...prevState.current,
        progress: event.target.duration,
        inProgress: false,
      };

      return prevState;
    });
  }

  listen () {
    // The premium URL of this excerpt may not exist yet: ask for it, then play.
    if (this.state.voice === 'arthur' && this.state.current.track && !this.currentSrc()) {
      this.ensurePremiumTrack(this.state.current.track).then(url => { if (url) this.listen(); });
      return;
    }

    if (this.refs.player.rap) {
      if (window.domEditor) window.domEditor.focus();

      if (this.state.current.track.type !== "dictation" && this.state[this.state.current.track.type].progress)
        this.setCurrentTime(this.state[this.state.current.track.type].progress);
      const playPromise = this.refs.player.rap.audioEl.current.play();

      // Prevent The play() request was interrupted error
      if (playPromise != undefined) {
        playPromise.then(_ => {})
        .catch(_ => {});
      }

      this.setState(prevState => ({
        current: {
          ...prevState.current,
          inProgress: true
        }
      }));
    }
  }

  play () {
    if (this.state.current.track && this.state.current.progress < this.state.current.duration) {
      this.listen();
    } else {
      this.next();
    }
  }

  playPause() {
    if (this.state.current.inProgress) {
      this.pause();
    } else {
      this.play();
    }
  }

  next () {
    if (!this.state.current.track  || this.state.current.track.type === "first_reading") {
      this.toggleListening(2, this.state.dictation[0][0]);
    } else if (this.state.current.track.type === "proof_reading") {
      this.setCurrentTime(0);
    } else {
      let sentence = this.state.current.track.sentence - 1;
      let tracks = this.state.dictation[sentence];
      let nextTrack = tracks.find(track => track.chunk == this.state.current.track.chunk + 1);

      if (!nextTrack) {
        sentence = sentence + 1;
        tracks = this.state.dictation[sentence];
        if (tracks) nextTrack = tracks[0]
      }

      if (nextTrack) { // Continue dictation
        this.toggleListening(sentence + 2, nextTrack);
      } else if (this.state.proof_reading) { // Proof reading
        this.toggleListening(this.state.dictation.length + 2, this.state.proof_reading);
      }
    }
  }

  prev () {
    if (this.state.current.song) {
      if (this.state.current.track.type === "proof_reading") { // If proof dictation -> take last dictation
        const tracks = this.state.dictation[this.state.dictation.length - 1];
        const track = tracks[tracks.length - 1];

        this.toggleListening(this.state.current.song - 1, track);
      } else if (this.state.current.track.type === "first_reading") { // If first dictation -> set current time
        this.setCurrentTime(0);
      } else if (this.state.current.track.sentence == 1 && this.state.current.track.chunk == 1) { // If first dictation
        this.toggleListening(1, this.state.first_reading);
      } else { // Get prev dictation
        const tracks = this.state.dictation[this.state.current.track.sentence - 1];
        const prevTrack = tracks.find(track => track.chunk == this.state.current.track.chunk - 1);
        if (prevTrack) {
          this.toggleListening(this.state.current.song, prevTrack);
        } else {
          const tracks = this.state.dictation[this.state.current.track.sentence - 2];
          const prevTrack = tracks[tracks.length - 1]

          this.toggleListening(this.state.current.song  - 1, prevTrack);
        }
      }
    }
  }

  pause () {
    if (this.refs.player.rap) {
      this.refs.player.rap.audioEl.current.pause();
      this.setState(prevState => ({
        current: {
          ...prevState.current,
          inProgress: false
        }
      }));
    }
  }

  setCurrentTime (time) {
    if (this.refs.player.rap) {
      this.refs.player.rap.audioEl.current.currentTime = time;
    }
  }

  isInProgress = () => this.state.current.inProgress;

  getNextSong () {
    return this.state.dictation.findIndex(tracks => tracks.find(track => !track.duration || !track.progress || (track.progress < track.duration && this.state.current.track.chunk < track.chunk && this.state.current.track.sentence < track.sentence))) + 1;
  }

  getNextTrack (index) {
    return this.state.dictation[index - 1].find(track => !track.duration || !track.progress || (track.progress < track.duration && this.state.current.track.chunk < track.chunk && this.state.current.track.sentence < track.sentence));
  };

  // A portal rather than a second component: the playlist is collapsed on
  // mobile, so the switcher is mirrored next to the text, sharing this
  // component's state instead of drifting from it.
  // The studio preview (TextEditor) passes no voices: it only has the default
  // recording and no endpoint to switch to, so there is nothing to offer.
  hasVoiceSwitcher = () => Array.isArray(this.props.voices) && this.props.voices.length > 0;

  renderInPageSwitcher = () => {
    if (!this.hasVoiceSwitcher()) return null;

    const target = document.getElementById('js-voice-switcher');
    if (!target) return null;

    return ReactDOM.createPortal(
      <VoiceSwitcher
        voice={this.state.voice}
        voices={this.props.voices}
        onChange={this.changeVoice}
        loading={this.state.loading}
        compact
      />,
      target
    );
  };

  render() {
    let index = 1;
    return (
      <div ref="playlist">
        <div className="text-center pt-1 md:pt-2 flex md:block justify-between items-center">
          <div className="inline-block align-middle js-tooltip">
            <button type="button" className="text-grey w-12 h-12 md:h-16 rounded-full pt-4 md:pt-0 mr-2 md:mx-2 text-2xl md:text-3xl transition duration-100 transform origin-center md:hover:scale-125 md:hover:text-white" onClick={() => this.prev()}>
              <i className="fas fa-arrow-to-left" />
            </button>
          </div>
          <div className="js-tooltip-element font-bold">
            Morceau précédent
            <div className="js-tooltip-arrow" data-popper-arrow></div>
          </div>
          <div className="inline-block align-middle js-tooltip">
            <button type="button" className="bg-success text-white h-10 px-4 md:px-0 md:w-16 md:h-16 rounded-full text-md md:text-2xl transition duration-100 transform origin-center md:hover:scale-125" onClick={() => this.isInProgress() ? this.pause() : this.play()}>
              <i className={`fas fa-${this.isInProgress() ? 'pause' : 'play'} inline-block align-middle`} /> <span className="inline-block md:hidden ml-1 font-semibold">{this.isInProgress() ? "Pause" : "Lire le texte"}</span>
            </button>
          </div>
          <div className="js-tooltip-element font-bold">
            Lecture
            <div className="js-tooltip-arrow" data-popper-arrow></div>
          </div>
          <div className="inline-block align-middle js-tooltip">
            <button type="button" className="text-grey w-12 h-12 md:h-16 rounded-full pt-4 md:pt-0 ml-2 md:mx-2 text-2xl md:text-3xl transition duration-100 transform origin-center md:hover:scale-125 md:hover:text-white" onClick={() => this.next()}>
              <i className="fas fa-arrow-to-right" />
            </button>
          </div>
          <div className="js-tooltip-element font-bold">
            Morceau suivant
            <div className="js-tooltip-arrow" data-popper-arrow></div>
          </div>
        </div>

        { this.hasVoiceSwitcher() && (
          <VoiceSwitcher voice={this.state.voice} voices={this.props.voices} onChange={this.changeVoice} loading={this.state.loading} />
        ) }

        {this.renderInPageSwitcher()}

        { this.hasVoiceSwitcher() && this.state.voice === 'margot' && (
          <div className="text-center mt-3">
            <button type="button" className="text-grey text-base transition duration-100 hover:text-white" onClick={this.suggestPremiumVoice}>
              <span className="mr-1">🤔</span>
              <span className="border-b border-dotted border-grey">Je n'ai pas compris ce qui a été dicté</span>
            </button>
          </div>
        ) }
        <ul className="playlist-song pt-4 px-2 md:px-0 md:pt-6">
          { this.props.first_reading && (
            <Song toggleListening={this.toggleListening} replay={this.replay} current={this.state.current} key={`song-${index}`} index={index} track={this.state.first_reading} setCurrentTime={this.setCurrentTime}>Écouter la dictée en entier <span className="text-sm text-grey">(recommandé)</span></Song>
          ) }
          { this.state.dictation.map((chunks) => {
              index++;
              return (
                <Song toggleListening={this.toggleListening} replay={this.replay} onCollapse={this.onCollapse} current={this.state.current} key={`song-${index}`} index={index} tracks={chunks}>Phrase n°{index-1}</Song>
              );
            })
          }
          { this.props.proof_reading && (
            <Song toggleListening={this.toggleListening} replay={this.replay} current={this.state.current} key={`song-${index+1}`} index={index+1} track={this.state.proof_reading} setCurrentTime={this.setCurrentTime}>Relire la dictée en entier <span className="text-sm text-grey">(recommandé)</span></Song>
          ) }
        </ul>
        <Player ref="player" state={this.state.current} src={this.currentSrc()} onLoadedMetadata={this.onLoadedMetadata} onPlay={this.onPlay} onListen={this.onListen} onEnded={this.onEnded}  />
      </div>
    );
  }
}

Playlist.propTypes = {
  dictation: PropTypes.array.isRequired,
  first_reading: PropTypes.object,
  proof_reading: PropTypes.object,
  premium_dictation: PropTypes.array,
  premium_first_reading: PropTypes.object,
  premium_proof_reading: PropTypes.object,
  voice: PropTypes.string,
  voices: PropTypes.array,
  corrected: PropTypes.bool,
  suggestions_enabled: PropTypes.bool,
  previews_left: PropTypes.number,
  text_id: PropTypes.string,
  audio_endpoint: PropTypes.string,
  voice_endpoint: PropTypes.string,
  paywall_html: PropTypes.string,
  suggestions_html: PropTypes.object
};

Playlist.defaultProps = {
  dictation: []
};

export default Playlist;
